// QuestionBankModal — Banco de preguntas reutilizable.
// Lista todas las preguntas existentes en la plataforma (de todas las
// plantillas CEP/CAD) y permite al admin seleccionar varias para importarlas
// como copia (id nuevo) al bloque destino. Opción A — biblioteca simple.
import { useEffect, useMemo, useState } from "react";
import { X, Search, Library, Loader2, CheckSquare, Square } from "lucide-react";
import { api } from "@/lib/api";
import { RenderTextWithMath } from "@/components/MathFormulaEditor";
import { toast } from "sonner";

const TYPE_LABEL = {
  multiple_choice: "Opción múltiple",
  numeric: "Numérica",
  open: "Texto abierto",
  open_text: "Texto abierto",
  fill_in_blank: "Completar",
  image_interactive: "Imagen interactiva",
  likert: "Likert",
  scale_1_10: "Escala 1-10",
  multi_part: "Multi-parte",
  equation: "Ecuación",
  stacked_op: "Operación apilada",
  table_input: "Tabla",
};

export default function QuestionBankModal({
  targetTemplateCode,
  targetBlockId,
  defaultKind = null,
  onClose,
  onImported,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterDomain, setFilterDomain] = useState("");
  const [filterKind, setFilterKind] = useState(defaultKind || "");
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (filterKind) params.kind = filterKind;
        if (filterType) params.q_type = filterType;
        if (filterDomain) params.domain = filterDomain;
        if (search.trim()) params.search = search.trim();
        const { data } = await api.get("/question-bank", { params });
        if (!alive) return;
        setItems(data.items || []);
      } catch {
        if (alive) toast.error("No se pudo cargar el banco de preguntas");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [filterKind, filterType, filterDomain, search]);

  const domains = useMemo(() => {
    const set = new Set();
    items.forEach((it) => { if (it.domain) set.add(it.domain); });
    return Array.from(set).sort();
  }, [items]);

  const types = useMemo(() => {
    const set = new Set();
    items.forEach((it) => { if (it.type) set.add(it.type); });
    return Array.from(set).sort();
  }, [items]);

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const importNow = async () => {
    if (selected.size === 0) {
      toast.warning("Selecciona al menos una pregunta para importar");
      return;
    }
    if (!targetTemplateCode || !targetBlockId) {
      toast.error("Falta plantilla / bloque destino");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/question-bank/import", {
        target_template: targetTemplateCode,
        target_block_id: targetBlockId,
        question_refs: Array.from(selected),
      });
      toast.success(`Se importaron ${data.inserted} pregunta(s) al bloque`);
      onImported && onImported(data.inserted);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo importar");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-[#1F2547]/60 backdrop-blur-sm grid place-items-center p-4"
      data-testid="question-bank-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="px-6 py-4 border-b border-[#E1E5F0] flex items-center justify-between bg-white">
          <div className="flex items-center gap-2">
            <Library size={20} className="text-[#A43790]" />
            <div>
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790]">Biblioteca</p>
              <h3 className="font-display text-lg font-extrabold text-[#1F2547]">Banco de preguntas</h3>
            </div>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid="bank-modal-close"><X size={20} /></button>
        </div>

        {/* Filtros */}
        <div className="px-6 py-3 bg-[#F7F8FA] border-b border-[#E1E5F0] flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7591]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por texto del enunciado…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm outline-none focus:border-[#A43790]"
              data-testid="bank-search"
            />
          </div>
          <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)}
            className="px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm font-medium" data-testid="bank-filter-kind">
            <option value="">Todos los tipos de plantilla</option>
            <option value="cep">CEP (emotivo-perceptual)</option>
            <option value="cad">CAD (académico)</option>
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm font-medium" data-testid="bank-filter-type">
            <option value="">Todos los formatos</option>
            {types.map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
          </select>
          {domains.length > 0 && (
            <select value={filterDomain} onChange={(e) => setFilterDomain(e.target.value)}
              className="px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm font-medium" data-testid="bank-filter-domain">
              <option value="">Todos los dominios</option>
              {domains.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto px-6 py-3" data-testid="bank-list">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#6E7591]">
              <Loader2 size={20} className="animate-spin mr-2" /> Cargando preguntas…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-[#6E7591] text-sm">
              No se encontraron preguntas con esos filtros.
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((it) => {
                const isSel = selected.has(it.ref);
                return (
                  <li key={it.ref}
                      className={`rounded-xl border-2 p-3 cursor-pointer transition-colors ${isSel ? "border-[#A43790] bg-[#FFF1F8]" : "border-[#E1E5F0] hover:border-[#A43790] bg-white"}`}
                      onClick={() => toggle(it.ref)}
                      data-testid={`bank-item-${it.ref}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {isSel
                          ? <CheckSquare size={16} className="text-[#A43790]" />
                          : <Square size={16} className="text-[#A6ACC1]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wider font-extrabold">
                          <span className="px-2 py-0.5 rounded-full bg-[#1F2547] text-white">{TYPE_LABEL[it.type] || it.type}</span>
                          <span className="px-2 py-0.5 rounded-full bg-[#E8EEFB] text-[#2C49A0]">{it.template_code}</span>
                          <span className="text-[#6E7591]">/ {it.block_title || it.block_id}</span>
                          {it.domain && (
                            <span className="px-2 py-0.5 rounded-full bg-[#FFF7DA] text-[#9C7A18]">{it.domain}</span>
                          )}
                        </div>
                        <p className="mt-1.5 text-sm text-[#1F2547] font-bold leading-snug line-clamp-3">
                          <RenderTextWithMath text={it.text || "(sin enunciado)"} />
                        </p>
                        {it.type === "multiple_choice" && it.options?.length > 0 && (
                          <ul className="mt-1.5 text-[11px] text-[#4C5478] space-y-0.5">
                            {it.options.slice(0, 4).map((o, oi) => (
                              <li key={oi} className="truncate">
                                <span className="inline-block w-4 h-4 rounded-full bg-[#1F2547] text-white text-[9px] font-extrabold text-center leading-4 mr-1">{String.fromCharCode(65 + oi)}</span>
                                <RenderTextWithMath text={o.label || ""} />
                                {o.is_correct && <span className="ml-1 text-[10px] text-[#3FA84D] font-extrabold">✓</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-[#E1E5F0] bg-white flex items-center gap-3 shrink-0">
          <p className="text-xs text-[#6E7591]" data-testid="bank-counter">
            <b className="text-[#1F2547]">{selected.size}</b> seleccionada(s) · {items.length} mostradas
          </p>
          <div className="flex-1" />
          <button onClick={onClose} className="jasq-btn-ghost text-sm" data-testid="bank-cancel">Cancelar</button>
          <button onClick={importNow} disabled={importing || selected.size === 0}
            className="jasq-btn-primary text-sm inline-flex items-center gap-1.5" data-testid="bank-import">
            {importing && <Loader2 size={12} className="animate-spin" />}
            Importar {selected.size > 0 ? `(${selected.size})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
